import React, { useState } from 'react';
import { UserPlus, Check, Clock, MessageCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useNotif } from '@/context/NotificationContext';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';

/**
 * Compact "Add Friend" button.
 * Props: userId (required), variant: 'icon' | 'pill', size: 'xs'|'sm'
 */
export default function AddFriendButton({ userId, userName = '', variant = 'icon', size = 'xs', onOpenChat }) {
  const { user } = useAuth();
  const { friendStatus, refresh, setOpenChatWith } = useNotif();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  if (!user || user === false) return null;
  if (!userId || userId === user.id) return null;

  const status = friendStatus(userId);

  const add = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setLoading(true);
    try {
      await api.post(`/friends/request/${userId}`);
      toast.success(t('messages.request_sent'));
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  const openChat = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (onOpenChat) onOpenChat({ user_id: userId, name: userName });
    else setOpenChatWith({ user_id: userId, name: userName });
  };

  const sizeCls = size === 'sm' ? 'h-7 w-7 text-[12px]' : 'h-6 w-6 text-[11px]';

  if (variant === 'pill') {
    if (status === 'accepted') {
      return (
        <button data-testid={`pill-msg-${userId}`} onClick={openChat}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition">
          <MessageCircle className="w-3 h-3" /> {t('friend.message')}
        </button>
      );
    }
    if (status === 'pending_out') {
      return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-white/5 text-white/40"><Clock className="w-3 h-3" /> {t('friend.pending')}</span>;
    }
    if (status === 'pending_in') {
      return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#FF007F]/10 text-[#FF007F]"><UserPlus className="w-3 h-3" /> {t('friend.respond')}</span>;
    }
    return (
      <button data-testid={`pill-add-${userId}`} onClick={add} disabled={loading}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 transition disabled:opacity-50">
        <UserPlus className="w-3 h-3" /> {t('friend.add')}
      </button>
    );
  }

  // icon variant
  if (status === 'accepted') {
    return (
      <button data-testid={`icon-msg-${userId}`} onClick={openChat}
        title={t('friend.message')}
        className={`inline-grid place-items-center rounded-full bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 transition ${sizeCls}`}>
        <MessageCircle className="w-3 h-3" />
      </button>
    );
  }
  if (status === 'pending_out') {
    return (
      <span title={t('friend.pending')} className={`inline-grid place-items-center rounded-full bg-white/5 text-white/40 ${sizeCls}`}>
        <Clock className="w-3 h-3" />
      </span>
    );
  }
  if (status === 'pending_in') {
    return (
      <span title={t('friend.respond')} className={`inline-grid place-items-center rounded-full bg-[#FF007F]/10 text-[#FF007F] ${sizeCls}`}>
        <Check className="w-3 h-3" />
      </span>
    );
  }
  return (
    <button data-testid={`icon-add-${userId}`} onClick={add} disabled={loading}
      title={t('friend.add')}
      className={`inline-grid place-items-center rounded-full bg-[#FF007F]/10 text-[#FF007F] hover:bg-[#FF007F]/20 transition disabled:opacity-50 ${sizeCls}`}>
      <UserPlus className="w-3 h-3" />
    </button>
  );
}
